import { Link } from "@tanstack/react-router";
import { Stars02 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type {
  OpenCollaborationInsightsFunnel,
  OpenCollaborationInsightsPerformance,
  OpenCollaborationInsightsRelationships,
  OpenCollaborationInsightsSummary,
  TargetCollaborationInsightsFunnel,
  TargetCollaborationInsightsPerformance,
  TargetCollaborationInsightsRelationships,
  TargetCollaborationInsightsSummary,
} from "@/api/collaborations";
import { useMerchant } from "@/providers/MerchantProvider";
import type { CreatorStatusTab } from "../collaboration-table-columns";
import { funnelKeyToStatusTab, performanceKeyToStatusTab, totalGmvStatusTab } from "./filter-mapping";
import { InsightsFunnelTab } from "./insights-funnel-tab";
import { InsightsPerformanceTab } from "./insights-performance-tab";
import { InsightsRelationshipsTab } from "./insights-relationships-tab";
import { InsightsSummaryCards } from "./insights-summary-cards";

type InsightsTab = "funnel" | "relationships" | "performance";
export type CollaborationKind = "target" | "open";

type QueryLike<T> = {
  data: T | null | undefined;
  isLoading: boolean;
};

const CREATORS_TABLE_ANCHOR_ID = "collaboration-creators-list";

function scrollToCreators() {
  const el = document.getElementById(CREATORS_TABLE_ANCHOR_ID);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function FunnelIcon({ active }: { active: boolean }) {
  const color = active ? "#6941C6" : "rgba(255,255,255,0.6)";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none" aria-hidden="true">
      <path
        d="M11.334 6.45825V9.00825C11.334 9.42829 11.334 9.63831 11.2522 9.79875C11.1803 9.93987 11.0656 10.0546 10.9245 10.1265C10.764 10.2083 10.554 10.2083 10.134 10.2083L0.833984 10.2083M8.33398 10.2083V12.7583C8.33398 13.1783 8.33398 13.3883 8.25224 13.5487C8.18033 13.6899 8.0656 13.8046 7.92448 13.8765C7.76404 13.9583 7.55402 13.9583 7.13398 13.9583H0.833984M0.833984 0.833252L0.833985 15.8333M0.833984 6.45825L13.134 6.45825C13.554 6.45825 13.764 6.45825 13.9245 6.37651C14.0656 6.3046 14.1803 6.18987 14.2522 6.04874C14.334 5.88831 14.334 5.67829 14.334 5.25825V3.90825C14.334 3.48821 14.334 3.27819 14.2522 3.11776C14.1803 2.97664 14.0656 2.8619 13.9245 2.79C13.764 2.70825 13.554 2.70825 13.134 2.70825L0.833985 2.70825L0.833984 6.45825Z"
        stroke={color}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CreatorBreakdownIcon({ active }: { active: boolean }) {
  const color = active ? "#6941C6" : "rgba(255,255,255,0.6)";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none" aria-hidden="true">
      <path
        d="M13.959 13.5833V8.33325M7.95898 13.5833V6.08325M1.95898 13.5833L1.95898 10.5833M9.01399 2.35388L12.8903 3.8075M7.05806 2.63394L2.85916 5.78312M14.7545 3.41276C15.1938 3.8521 15.1938 4.56441 14.7545 5.00375C14.3151 5.44309 13.6028 5.44309 13.1635 5.00375C12.7242 4.56441 12.7242 3.8521 13.1635 3.41276C13.6028 2.97342 14.3151 2.97342 14.7545 3.41276ZM2.75448 5.66276C3.19382 6.1021 3.19382 6.81441 2.75448 7.25375C2.31514 7.69309 1.60283 7.69309 1.16349 7.25375C0.724149 6.81441 0.724149 6.1021 1.16349 5.66276C1.60283 5.22342 2.31514 5.22342 2.75448 5.66276ZM8.75448 1.16276C9.19382 1.6021 9.19382 2.31441 8.75448 2.75375C8.31514 3.19309 7.60283 3.19309 7.16349 2.75375C6.72415 2.31441 6.72415 1.6021 7.16349 1.16276C7.60283 0.723417 8.31514 0.723417 8.75448 1.16276Z"
        stroke={color}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PerformanceIcon({ active }: { active: boolean }) {
  const color = active ? "#6941C6" : "rgba(255,255,255,0.6)";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none" aria-hidden="true">
      <path
        d="M8.33398 10.5833V14.3333M12.834 14.3333L9.87043 11.8636C9.32384 11.4081 9.05055 11.1804 8.74548 11.0934C8.47652 11.0166 8.19145 11.0166 7.92249 11.0934C7.61742 11.1804 7.34413 11.4081 6.79754 11.8636L3.83398 14.3333M5.33398 6.83325V7.58325M8.33398 5.33325V7.58325M11.334 3.83325V7.58325M15.834 0.833252H0.833984M1.58398 0.833252H15.084V6.98325C15.084 8.24337 15.084 8.87343 14.8387 9.35473C14.623 9.77809 14.2788 10.1223 13.8555 10.338C13.3742 10.5833 12.7441 10.5833 11.484 10.5833H5.18398C3.92387 10.5833 3.29381 10.5833 2.81251 10.338C2.38914 10.1223 2.04493 9.77809 1.82922 9.35473C1.58398 8.87343 1.58398 8.24337 1.58398 6.98325V0.833252Z"
        stroke={color}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const TABS: {
  id: InsightsTab;
  label: string;
  Icon: React.ComponentType<{ active: boolean }>;
}[] = [
  { id: "funnel", label: "Overview Funnel", Icon: FunnelIcon },
  { id: "relationships", label: "Creator Breakdown", Icon: CreatorBreakdownIcon },
  { id: "performance", label: "Performance", Icon: PerformanceIcon },
];

function InsightsEmptyState({
  collaborationId,
  collaborationType,
}: {
  collaborationId: string | undefined;
  collaborationType: CollaborationKind;
}) {
  const merchant = useMerchant();
  const isOpen = collaborationType === "open";
  return (
    <div className="flex-1 flex items-center overflow-hidden relative">
      {/* Decorative image — bottom-right corner */}
      <img
        src="/insights-empty-state-background.png"
        alt=""
        aria-hidden="true"
        className="absolute bottom-0 right-0 pointer-events-none select-none"
        style={{ maxWidth: "62%", maxHeight: "85%", width: "auto", height: "auto" }}
      />

      {/* Content */}
      <div className="relative z-10 px-8 py-10 flex flex-col gap-4 max-w-xs">
        <div className="flex flex-col gap-2">
          <p className="text-xl font-semibold text-white">{isOpen ? "No creators yet" : "No creators invited"}</p>
          <p className="text-sm text-white/60 leading-relaxed">
            {isOpen ? (
              <>
                Any creator on TikTok can pick up <br /> this collaboration. The ones who <br /> do will show up here
              </>
            ) : (
              <>
                Get going quickly with our <br /> recommended users carefully <br /> selected for this collaboration
              </>
            )}
          </p>
        </div>
        {!isOpen && collaborationId && (
          <Link
            to="/merchant/$merchantId/collaborations/target/$collaborationId/recommendations"
            params={{ merchantId: merchant, collaborationId }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white w-fit transition-opacity hover:opacity-90"
            style={{ background: "linear-gradient(90deg, #7F56D9 0%, #9E77ED 100%)" }}
          >
            <Stars02 className="w-4 h-4" />
            See Recommended Creators
          </Link>
        )}
      </div>
    </div>
  );
}

export type CollaborationInsightsPanelViewProps = {
  summaryQuery: QueryLike<TargetCollaborationInsightsSummary | OpenCollaborationInsightsSummary>;
  funnelQuery: QueryLike<TargetCollaborationInsightsFunnel | OpenCollaborationInsightsFunnel>;
  relationshipsQuery: QueryLike<TargetCollaborationInsightsRelationships | OpenCollaborationInsightsRelationships>;
  performanceQuery: QueryLike<TargetCollaborationInsightsPerformance | OpenCollaborationInsightsPerformance>;
  /** A single concrete collaboration id, used for the "Find More Creators" recommendations link. */
  recommendationsCollaborationId: string | undefined;
  collaborationType: CollaborationKind;
  showRecommendations?: boolean;
  /** Whether the Performance tab shows the Invited/Accepted toggle. */
  showModeToggle: boolean;
  performanceMode: "invited" | "accepted";
  onPerformanceModeChange: (mode: "invited" | "accepted") => void;
  onFilterChange?: (status: CreatorStatusTab) => void;
  onBreakdownFilterChange?: (bucket: string | null) => void;
};

export function CollaborationInsightsPanelView({
  summaryQuery,
  funnelQuery,
  relationshipsQuery,
  performanceQuery,
  recommendationsCollaborationId,
  collaborationType,
  showRecommendations,
  showModeToggle,
  performanceMode,
  onPerformanceModeChange,
  onFilterChange,
  onBreakdownFilterChange,
}: CollaborationInsightsPanelViewProps) {
  const [activeTab, setActiveTab] = useState<InsightsTab>("funnel");

  const [funnelSelection, setFunnelSelection] = useState<string | null>(null);
  const [breakdownSelection, setBreakdownSelection] = useState<string | null>(null);
  const [perfSelection, setPerfSelection] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab !== "funnel") setFunnelSelection(null);
    if (activeTab !== "relationships") {
      setBreakdownSelection(null);
      onBreakdownFilterChange?.(null);
    }
    if (activeTab !== "performance") setPerfSelection(null);
  }, [activeTab, onBreakdownFilterChange]);

  useEffect(() => {
    if (performanceMode === "invited") setPerfSelection(null);
  }, [performanceMode]);

  const isEmpty = !summaryQuery.isLoading && (summaryQuery.data?.totalCreators ?? 0) === 0;

  const handleFunnelSelect = (key: string | null) => {
    setFunnelSelection(key);
    setPerfSelection(null);
    if (key === null) {
      onFilterChange?.("all");
      return;
    }
    const status = funnelKeyToStatusTab(key, collaborationType);
    if (status) onFilterChange?.(status);
  };

  const handlePerfSelect = (key: string | null) => {
    setPerfSelection(key);
    setFunnelSelection(null);
    if (performanceMode === "invited") return;
    if (key === null) {
      onFilterChange?.("all");
      return;
    }
    const status = performanceKeyToStatusTab(key, collaborationType);
    if (status) {
      onFilterChange?.(status);
      window.setTimeout(scrollToCreators, 50);
    }
  };

  const handleBreakdownSelect = (key: string | null) => {
    setBreakdownSelection(key);
    setFunnelSelection(null);
    setPerfSelection(null);
    onBreakdownFilterChange?.(key);
    if (key !== null) {
      onFilterChange?.("all");
    }
  };

  const handleTotalGmvClick = onFilterChange
    ? () => {
        setFunnelSelection("generated_gmv");
        setPerfSelection(null);
        onFilterChange(totalGmvStatusTab(collaborationType));
        window.setTimeout(scrollToCreators, 50);
      }
    : undefined;

  return (
    <div className="flex rounded-xl overflow-hidden mb-6 shadow-sm" style={{ minHeight: 400 }}>
      {/* Left panel — summary KPIs with background image */}
      <div
        className="shrink-0 px-6 py-8 flex flex-col bg-cover bg-center bg-no-repeat"
        style={{
          minWidth: 400,
          backgroundImage: "url('/left-panel-collaboration-background.png')",
        }}
      >
        <InsightsSummaryCards
          data={summaryQuery.data}
          isLoading={summaryQuery.isLoading}
          onTotalGmvClick={handleTotalGmvClick}
        />
      </div>

      {/* Right panel — dark purple gradient */}
      <div
        className="flex-1 flex flex-col min-w-0"
        style={{ background: "linear-gradient(160deg, #322047 0%, #4B2E61 50%, #3D2653 100%)" }}
      >
        {isEmpty ? (
          <InsightsEmptyState collaborationId={recommendationsCollaborationId} collaborationType={collaborationType} />
        ) : (
          <>
            {/* Tab headers */}
            <div className="flex items-center gap-2 px-8 pt-6 pb-0">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                    style={
                      isActive
                        ? { background: "#F9F5FF", color: "#6941C6" }
                        : { background: "transparent", color: "rgba(255,255,255,0.6)" }
                    }
                  >
                    <tab.Icon active={isActive} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab body — all three panels are always in layout (grid-stack) so
                the container height equals the tallest panel and never collapses.
                Inactive panels use opacity:0 + pointerEvents:none; opacity is
                GPU-composited so the toggle is paint-free and produces no flash. */}
            <div className="px-8 pb-6 pt-8" style={{ display: "grid", gridTemplateAreas: '"panel"' }}>
              {[
                {
                  id: "funnel" as const,
                  node: (
                    <InsightsFunnelTab
                      data={funnelQuery.data}
                      isLoading={funnelQuery.isLoading}
                      selectedKey={funnelSelection}
                      onSelect={onFilterChange ? handleFunnelSelect : undefined}
                      totalCreators={summaryQuery.data?.totalCreators}
                    />
                  ),
                },
                {
                  id: "relationships" as const,
                  node: (
                    <InsightsRelationshipsTab
                      collaborationId={recommendationsCollaborationId ?? ""}
                      data={relationshipsQuery.data}
                      isLoading={relationshipsQuery.isLoading}
                      showRecommendations={showRecommendations}
                      selectedKey={breakdownSelection}
                      onSelect={handleBreakdownSelect}
                    />
                  ),
                },
                {
                  id: "performance" as const,
                  node: (
                    <InsightsPerformanceTab
                      collaborationId={recommendationsCollaborationId ?? ""}
                      data={performanceQuery.data}
                      isLoading={performanceQuery.isLoading}
                      mode={performanceMode}
                      onModeChange={onPerformanceModeChange}
                      showRecommendations={showRecommendations}
                      showModeToggle={showModeToggle}
                      selectedKey={perfSelection}
                      onSelect={onFilterChange ? handlePerfSelect : undefined}
                    />
                  ),
                },
              ].map(({ id, node }) => {
                const isActive = activeTab === id;
                return (
                  <div
                    key={id}
                    style={{
                      gridArea: "panel",
                      opacity: isActive ? 1 : 0,
                      pointerEvents: isActive ? "auto" : "none",
                    }}
                  >
                    {node}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
